<template>
  <v-dialog
      v-model="open"
      width="700px"
      overlay-opacity="0.9"
      persistent
  >
    <v-card class="view">
      <v-card-title class="headline orange--text">
        <v-icon color="orange" large left>mdi-star</v-icon>
        ยืนยันการแลกแต้ม
      </v-card-title>

      <v-card-text>
        <v-container v-if="order">
          <!-- Shipping Address -->
          <v-row>
            <v-col cols="12">
              <h3 class="mb-2">ที่อยู่จัดส่ง</h3>
              <div v-if="shippingAddress" class="address-box pa-3">
                <div><strong>{{ shippingAddress.name }}</strong></div>
                <div>{{ shippingAddress.address }}</div>
                <div>
                  {{ shippingAddress.district && shippingAddress.district.name }}
                  {{ shippingAddress.city && shippingAddress.city.name }}
                  {{ shippingAddress.province && shippingAddress.province.name }}
                  {{ shippingAddress.zip_code }}
                </div>
                <div v-if="shippingAddress.contact_tel">
                  โทร: {{ shippingAddress.contact_tel }}
                </div>
              </div>
              <div v-else-if="order.customer" class="address-box pa-3">
                <div><strong>{{ order.customer.nickname }}</strong></div>
                <div>{{ order.customer.address }}</div>
                <div>
                  {{ order.customer.district }}
                  {{ order.customer.province }}
                </div>
                <div v-if="order.customer.erp_tel">
                  โทร: {{ order.customer.erp_tel }}
                </div>
              </div>
              <div v-else class="error--text">
                ไม่ได้เลือกที่อยู่จัดส่ง
              </div>
            </v-col>
          </v-row>

          <!-- Point Deduction Preview -->
          <v-row class="mt-2">
            <v-col cols="12">
              <h3 class="mb-2">แต้มที่จะถูกหัก</h3>
              <div v-if="loading" class="text-center pa-4">
                <v-progress-circular indeterminate color="orange"></v-progress-circular>
                <div class="mt-2">กำลังโหลด...</div>
              </div>
              <div v-else-if="deductionPreview.length > 0">
                <v-simple-table dense class="mb-2">
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left">ไตรมาส</th>
                        <th class="text-left">หมดอายุ</th>
                        <th class="text-right">คงเหลือ</th>
                        <th class="text-right orange--text">จะหัก</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(deduction, index) in deductionPreview" :key="index">
                        <td>Q{{ deduction.quarter }}/{{ deduction.year }}</td>
                        <td>{{ formatDate(deduction.spending_end) }}</td>
                        <td class="text-right">{{ number_format(deduction.balance, 2) }}</td>
                        <td class="text-right orange--text">
                          <strong>-{{ number_format(deduction.will_deduct, 2) }}</strong>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
                <div v-if="insufficientPoints" class="error--text pa-2">
                  ⚠️ แต้มไม่เพียงพอ! ต้องการ {{ number_format(order.total_point, 2) }} แต้ม แต่มีเพียง {{ number_format(availablePoints, 2) }} แต้ม
                </div>
              </div>
              <div v-else class="grey--text pa-3">
                ไม่มีแต้มที่จะถูกหัก
              </div>
            </v-col>
          </v-row>

          <!-- Total Points -->
          <v-row class="mt-2">
            <v-col cols="12">
              <h3 class="mb-2">ยอดรวม</h3>
              <div class="total-box pa-3 text-h5">
                <v-icon color="orange" large>mdi-star</v-icon>
                {{ number_format(order.total_point || 0) }} แต้ม
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn class="mr-4" color="gray" text @click="onCancel()">
          ยกเลิก
        </v-btn>
        <v-btn
            class="orange white--text"
            :disabled="insufficientPoints"
            @click="onConfirm()">
          <v-icon left>mdi-star</v-icon>
          ยืนยันแลกแต้ม
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style lang="scss" scoped>
.view {
  position: relative;
}

.address-box {
  background-color: #f5f5f5;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
}

.total-box {
  background-color: #fff3e0;
  border-radius: 4px;
  border: 2px solid #ff9800;
  text-align: center;
  font-weight: bold;
  color: #ff6f00;
}
</style>

<script lang="ts">
import {Component, Vue} from 'vue-property-decorator'
import {Order} from '../models/Order'
import Helper from '../services/Helper.js'

interface DeductionPreview {
  id: number
  quarter: number
  year: number
  balance: number
  spending_end: string
  will_deduct: number
}

@Component({
  components: {},
})
export default class OrderConfirmDialogPoint extends Vue {
  open = false
  order: Order | null = null
  onApply: ((order: Order) => void) | null = null
  loading = false
  deductionPreview: DeductionPreview[] = []
  availablePoints = 0

  number_format = Helper.number_format

  get shippingAddress() {
    if (!this.order || !this.order.shipping_address_id) {
      return null
    }

    if (this.order.shipping_address) {
      return this.order.shipping_address
    }

    if (this.order.customer && this.order.customer.shipping_addresses) {
      const addresses = this.order.customer.shipping_addresses.filter(
        (addr) => addr.id === this.order!.shipping_address_id
      )
      return addresses.length > 0 ? addresses[0] : null
    }

    return null
  }

  get insufficientPoints(): boolean {
    return this.availablePoints < (this.order?.total_point || 0)
  }

  mounted() {
    this.$root.$on('openOrderConfirmPoint', this.onOpen)
  }

  beforeDestroy() {
    this.$root.$off('openOrderConfirmPoint', this.onOpen)
  }

  async onOpen(params: { order: Order; onApply: (order: Order) => void }) {
    this.order = params.order
    this.onApply = params.onApply
    this.open = true

    // Load point deduction preview
    await this.loadDeductionPreview()
  }

  async loadDeductionPreview() {
    if (!this.order || !this.order.customer) {
      return
    }

    this.loading = true
    try {
      const response = await Helper.post('/api/loyalty/preview-deduction', {
        customer_id: this.order.customer.id,
        points: this.order.total_point || 0
      })

      if (response.data && response.data.data) {
        this.deductionPreview = response.data.data.deductions
        this.availablePoints = response.data.data.available_points
      }
    } catch (e) {
      console.error('Failed to load deduction preview:', e)
      this.$root.$emit('onError', {error: e})
    } finally {
      this.loading = false
    }
  }

  formatDate(date: string): string {
    if (!date) return ''
    const d = new Date(date)
    return d.toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' })
  }

  onCancel() {
    this.open = false
    this.order = null
    this.onApply = null
    this.deductionPreview = []
    this.availablePoints = 0
  }

  onConfirm() {
    if (this.onApply && this.order && !this.insufficientPoints) {
      this.onApply(this.order)
    }
    this.open = false
    this.order = null
    this.onApply = null
    this.deductionPreview = []
    this.availablePoints = 0
  }
}
</script>
